/**
 * reservationChatRealtime.ts — 自社チャット（LP）の Pusher イベントを共有一覧に反映する純関数
 *
 * 設計: md/master_md/page-line-chat.md「リアルタイム」／ booking-platform/md/customer-chat.md §11
 *
 * LP（new-n-car）は `private-admin-chat` に全会話のイベントを流す（new-message / read-updated / session-archived /
 * session-unarchived / sessions-archived-bulk / admin-handling-updated / ai-shadow-ready）。那覇と宮古は同じ Pusher
 * アプリ・同じチャンネルなので、payload の `store`（naha / miyako）で自店舗のものだけを採る。
 * React・pusher-js に依存しないので、ここに置いた判定はすべて単体テストで固定する。
 */
import { isReservationSessionKey, type ReservationChatSession, type ReservationChatMessage, type ReservationChatAttachment } from './reservationChat';

export const RESV_CHAT_ADMIN_CHANNEL = 'private-admin-chat';

/** pusher-js の channelAuthorization（ajax）。chat.php は socket_id / channel_name を form で受け、Bearer を検証して署名を返す */
export function buildChatChannelAuthorization(chatApiUrl: string, token: string | null) {
  if (!chatApiUrl || !token) return null;
  return {
    transport: 'ajax' as const,
    endpoint: `${chatApiUrl}?action=pusher_auth`,
    headers: { Authorization: `Bearer ${token}` },
  };
}

/**
 * このイベントを自店舗の LP チャット（予約チャット・匿名チャット）として扱うか。
 * 2026-10-26（/admin の統合・md/customer-chat.md §13）から匿名チャットも採る。store が無い＝旧 PHP は session_key だけで判断
 */
export function isOwnLpChatEvent(data: any, storeKey: string): boolean {
  if (!data || typeof data !== 'object') return false;
  if (typeof data.session_key !== 'string' || data.session_key === '') return false;
  if (data.store != null && String(data.store) !== storeKey) return false;
  return true;
}

export interface RealtimeApplyResult {
  sessions: ReservationChatSession[];
  /** 一覧に無い会話・一括アーカイブなど、API から取り直すべきとき true */
  refetch: boolean;
  /** 開いているパネルへ渡す新着メッセージ（new-message のとき） */
  message: (ReservationChatMessage & { session_key: string; attachment?: ReservationChatAttachment | null }) | null;
  /** 顧客の新着（通知音の判定に使う。silent は鳴らさない） */
  customerNew: boolean;
}

const same = (sessions: ReservationChatSession[]): RealtimeApplyResult => ({ sessions, refetch: false, message: null, customerNew: false });

/**
 * イベントを共有一覧に適用する。対象外（他店舗）は何も変えない。
 * 一覧に無い会話のイベント（新しい会話）は refetch=true で API に取り直させる。
 */
export function applyRealtimeEvent(
  sessions: ReservationChatSession[],
  eventName: string,
  data: any,
  storeKey: string,
): RealtimeApplyResult {
  if (eventName === 'staff-archive-updated') {
    // スタッフのアーカイブ（一覧から外す／戻す・§13-5）。session_key は配列で来る
    if (!data || typeof data !== 'object' || (data.store != null && String(data.store) !== storeKey)) return same(sessions);
    const keys = new Set<string>(Array.isArray(data.session_keys) ? data.session_keys.map((k: unknown) => String(k)) : []);
    if (keys.size === 0) return same(sessions);
    const at = typeof data.staff_archived_at === 'string' && data.staff_archived_at ? data.staff_archived_at : null;
    let changed = false;
    const next = sessions.map((s) => {
      if (!keys.has(s.session_key) || (s.staff_archived_at || null) === at) return s;
      changed = true;
      return { ...s, staff_archived_at: at };
    });
    return changed ? { sessions: next, refetch: false, message: null, customerNew: false } : same(sessions);
  }
  if (eventName === 'sessions-archived-bulk') {
    if (data && data.store != null && String(data.store) !== storeKey) return same(sessions);
    return { sessions, refetch: true, message: null, customerNew: false };
  }
  if (!isOwnLpChatEvent(data, storeKey)) return same(sessions);
  const key = String(data.session_key);
  const idx = sessions.findIndex((s) => s.session_key === key);

  if (eventName === 'new-message') {
    const id = Number(data.id || 0);
    const message = {
      id,
      sender: String(data.sender || 'user'),
      text: String(data.text ?? ''),
      timestamp: String(data.timestamp || ''),
      original_text: data.original_text ?? null,
      original_lang: data.original_lang ?? null,
      translated_text: data.translated_text ?? null,
      translated_lang: data.translated_lang ?? null,
      auto_translated: data.auto_translated ?? 0,
      attachment: data.attachment ?? null,
      session_key: key,
    };
    const customerNew = message.sender === 'user' && !data.silent;
    if (idx < 0) return { sessions, refetch: true, message, customerNew };
    const cur = sessions[idx];
    if (id && Number(cur.last_message_id || 0) >= id) {
      // 既に取り込んだ（ポーリングが先に拾った）イベント。一覧は変えないがパネルには渡す（パネル側で重複排除）
      return { sessions, refetch: false, message, customerNew: false };
    }
    const next: ReservationChatSession = {
      ...cur,
      last_message: message.text,
      last_sender: message.sender,
      last_message_id: id || cur.last_message_id,
      updated_at: message.timestamp || cur.updated_at,
      ...(message.sender === 'user' ? { last_user_at: message.timestamp || cur.last_user_at } : {}),
      // 匿名チャット: お客様の自由入力（silent でない）は要対応に上げる（LP 管理画面 adminChatStore と同じ。確定値は次の取得で LP の triage）
      ...(message.sender === 'user' && !data.silent && !isReservationSessionKey(key) ? { triage: 'attention' } : {}),
      // お客様の新着で、スタッフが一覧から外していた会話を戻す（LP もサーバー側で staff_archived_at を消している）
      ...(message.sender === 'user' ? { staff_archived_at: null } : {}),
    };
    return { sessions: replaceAt(sessions, idx, next), refetch: false, message, customerNew };
  }

  if (idx < 0) return { sessions, refetch: true, message: null, customerNew: false };
  const cur = sessions[idx];

  if (eventName === 'read-updated') {
    const lastRead = Number(data.last_read_id || 0);
    if (data.role === 'admin') {
      if (lastRead <= Number(cur.admin_last_read_id || 0)) return same(sessions);
      return {
        sessions: replaceAt(sessions, idx, { ...cur, admin_last_read_id: lastRead, admin_last_read_at: data.last_read_at ?? cur.admin_last_read_at }),
        refetch: false, message: null, customerNew: false,
      };
    }
    if (data.role === 'user') {
      if (lastRead <= Number(cur.user_last_read_id || 0)) return same(sessions);
      return {
        sessions: replaceAt(sessions, idx, { ...cur, user_last_read_id: lastRead, user_last_read_at: data.last_read_at ?? cur.user_last_read_at }),
        refetch: false, message: null, customerNew: false,
      };
    }
    return same(sessions);
  }
  if (eventName === 'session-archived') {
    return { sessions: replaceAt(sessions, idx, { ...cur, status: 'ARCHIVED' }), refetch: false, message: null, customerNew: false };
  }
  if (eventName === 'session-unarchived') {
    return { sessions: replaceAt(sessions, idx, { ...cur, status: 'ACTIVE' }), refetch: false, message: null, customerNew: false };
  }
  if (eventName === 'admin-handling-updated') {
    return {
      sessions: replaceAt(sessions, idx, { ...cur, admin_handling: Boolean(data.admin_handling), admin_handling_until: data.admin_handling_until ?? null }),
      refetch: false, message: null, customerNew: false,
    };
  }
  return same(sessions); // ai-shadow-ready など: 一覧には関係ない
}

const replaceAt = <T,>(arr: T[], i: number, v: T): T[] => arr.map((x, j) => (j === i ? v : x));

/** ポーリング間隔: 接続中は保険として長め、未接続はこれまでどおり */
export const RESV_LIST_POLL_MS = { connected: 60000, disconnected: 30000 } as const;
export const RESV_PANEL_POLL_MS = { connected: 60000, disconnected: 5000 } as const;
export const listPollInterval = (connected: boolean): number => (connected ? RESV_LIST_POLL_MS.connected : RESV_LIST_POLL_MS.disconnected);
export const panelPollInterval = (connected: boolean): number => (connected ? RESV_PANEL_POLL_MS.connected : RESV_PANEL_POLL_MS.disconnected);
